import React, {useEffect, useMemo, useState} from 'react';
import {
	EconomicProduct,
	ExportInvoiceModalProps,
	InvoiceEntry,
	InvoiceEntryRow,
	OnErrorType,
	QuickbookProductService,
	TaxCode,
	XeroAccountItem,
} from './ExportInvoiceModal.type';
import {
	ButtonDropdown,
	ButtonDropdownField,
	Checkbox,
	DropdownList,
	FlexColumn,
	FlexRow,
	Heading,
	IconTooltip,
	Modal,
	SubHeading,
	Table,
	Text,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer} from 'react-relay';
import {exportInvoiceModalFragment} from './ExportInvoiceModal.query';
import Util from '../../../forecast-app/shared/util/util';
import {getNotNullishNodes} from '../../../forecast-app/shared/util/NotNullPredicate';
import {fetchXeroAccounts, onExportXeroInvoice} from './ExportInvoiceModal.xero';
import {hideLoader, showLoader} from '../../global_loader';
import {INVOICE_INTEGRATIONS} from '../../../forecast-app/admin-tab/finance-page/ts/InvoiceIntegrationsUtils';
import {
	exportInvoiceToQuickbooksOnline,
	fetchQuickbooksData,
	getInitialQuickbookSettings,
} from './ExportInvoiceModal.quickbooks';
import {exportInvoiceToEconomics, fetchEconomicProducts} from './ExportInvoiceModal.economic';

const ExportInvoiceModal = ({closeModal, viewer}: ExportInvoiceModalProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;

	const noSpace = true;

	const {invoice, billTo} = viewer;
	const {client, currency} = invoice || {};

	const currencySymbol = useMemo(() => Util.GetCurrencySymbol(currency), [invoice?.currency]);

	const mapInvoiceEntryToRow = (
		invoiceEntry: InvoiceEntry,
		xeroAccount?: XeroAccountItem | null,
		quickbookProductService?: QuickbookProductService | null,
		economicProduct?: EconomicProduct | null
	): InvoiceEntryRow => {
		const taxRate = xeroAccount?.taxRate || 0;

		const row: InvoiceEntryRow = {
			...invoiceEntry,
			projectColor: invoiceEntry.project?.projectColor || '',
			projectId: `P${invoiceEntry.project?.companyProjectId}`,
			customProjectId: invoiceEntry.project?.customProjectId,
			entryDescription: invoiceEntry.description,
			discountPercentage: (invoiceEntry.discount || 0) / 100,
			tax: taxRate,
			taxPercentage: taxRate / 100,
			totalPrice: Util.getInvoiceEntryAmount(
				invoiceEntry.unitPrice,
				invoiceEntry.quantity,
				1,
				invoiceEntry.discount,
				taxRate
			),
		};

		if (xeroAccount) {
			row.label = xeroAccount.label;
			row.xeroAccount = xeroAccount.value;
			row.accountCode = xeroAccount.code;
		}

		if (quickbookProductService) {
			row.label = quickbookProductService.label;
			row.entryDescription = quickbookProductService.description;
			row.quickbooksItem = quickbookProductService.value;
			row.quickbooksTaxable = quickbookProductService.taxable;
			row.quickbooksUnitPrice = quickbookProductService?.unitPrice;
		}

		if (economicProduct) {
			row.label = economicProduct.label;
			row.economicProduct = economicProduct.value;
		}

		return row;
	};

	const [loading, setLoading] = useState<boolean>(true);
	const [isReadyForExport, setIsReadyForExport] = useState<boolean>(false);

	useEffect(() => {
		if (loading) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading]);

	// INTEGRATION
	const isXeroInvoice = useMemo(
		() => billTo?.billFrom?.integration === INVOICE_INTEGRATIONS.XERO,
		[billTo?.billFrom?.integration]
	);
	const isQuickbooksInvoice = useMemo(
		() => billTo?.billFrom?.integration === INVOICE_INTEGRATIONS.QUICKBOOKS,
		[billTo?.billFrom?.integration]
	);
	const isEconomicInvoice = useMemo(
		() => billTo?.billFrom?.integration === INVOICE_INTEGRATIONS.ECONOMIC,
		[billTo?.billFrom?.integration]
	);

	// INVOICE
	const initialInvoiceEntries = getNotNullishNodes(invoice?.entries?.edges);
	const [invoiceRows, setInvoiceRows] = useState<InvoiceEntryRow[]>(
		() => initialInvoiceEntries.map(entry => mapInvoiceEntryToRow(entry)) || []
	);
	const invoiceTotals = useMemo(() => Util.getInvoiceTotals(invoiceRows), [invoiceRows]);

	// XERO
	const [projectInDescription, setProjectInDescription] = useState<boolean>(false);
	const [selectedXeroAccount, setSelectedXeroAccount] = useState<XeroAccountItem | null>(null);
	const [xeroAccounts, setXeroAccounts] = useState<XeroAccountItem[]>([]);

	// QUICKBOOKS
	const [quickbooksTaxCodes, setQuickbooksTaxCodes] = useState<TaxCode[]>([]);
	const [selectedQuickbooksCode, setSelectedQuickbooksCode] = useState<TaxCode | null>(null);
	const [quickbooksProductsServices, setQuickbooksProductsServices] = useState<QuickbookProductService[]>([]);
	const [selectedQuickbooksProductService, setSelectedQuickbooksProductService] = useState<QuickbookProductService | null>(
		null
	);
	const [quickbookSettingsCount, setQuickbookSettingsCount] = useState<number>(0);
	const [quickbookSettings, setQuickbookSettings] = useState(getInitialQuickbookSettings(intl));

	// E-CONOMIC
	const [economicProducts, setEconomicProducts] = useState<EconomicProduct[]>([]);
	const [selectedEconomicProduct, setSelectedEconomicProduct] = useState<EconomicProduct | null>(null);

	const dropdownColumnLabel = useMemo(() => {
		if (isXeroInvoice) {
			return formatMessage({id: 'integrations.xero.xero_account'});
		}

		if (isQuickbooksInvoice) {
			return formatMessage({id: 'integrations.quickbooks.item_or_service'});
		}

		if (isEconomicInvoice) {
			return formatMessage({id: 'integrations.economic.product'});
		}

		return 'default';
	}, [isXeroInvoice, isQuickbooksInvoice, isEconomicInvoice]);

	useEffect(() => {
		setQuickbookSettingsCount(quickbookSettings.filter(item => item.checked).length);
	}, [quickbookSettings]);

	useEffect(() => {
		setInvoiceRows(
			initialInvoiceEntries.map(entry =>
				mapInvoiceEntryToRow(entry, selectedXeroAccount, selectedQuickbooksProductService, selectedEconomicProduct)
			) || []
		);
	}, [selectedXeroAccount, selectedQuickbooksProductService, selectedEconomicProduct]);

	useEffect(() => {
		if (billTo) {
			setLoading(true);

			if (isXeroInvoice) {
				fetchXeroAccounts(billTo, (accounts: XeroAccountItem[]) => {
					setXeroAccounts(accounts);
					setLoading(false);
				});
			} else if (isQuickbooksInvoice) {
				fetchQuickbooksData(
					(taxCodes: TaxCode[]) => setQuickbooksTaxCodes(taxCodes),
					(taxCode: TaxCode | null) => setSelectedQuickbooksCode(taxCode),
					(productsServices: QuickbookProductService[]) => setQuickbooksProductsServices(productsServices),
					() => setLoading(false),
					billTo.billFrom?.externalId || ''
				);
			} else if (isEconomicInvoice) {
				fetchEconomicProducts(products => {
					setEconomicProducts(products);
					setLoading(false);
				});
			}
		}
	}, [billTo]);

	const getMappedInvoiceRows = (
		row,
		xeroAccount: XeroAccountItem | null,
		productService: QuickbookProductService | null,
		economicProduct: EconomicProduct | null
	) => {
		return (
			invoiceRows.map(entry => {
				if (entry.id === row.id) {
					return mapInvoiceEntryToRow(entry, xeroAccount, productService, economicProduct);
				}

				return entry;
			}) || []
		);
	};

	const selectExpenseXeroAccount = (row, xeroAccount: XeroAccountItem) => {
		setInvoiceRows(getMappedInvoiceRows(row, xeroAccount, null, null));
	};

	const selectQuickBooksProductService = (row, productService: QuickbookProductService) => {
		setInvoiceRows(getMappedInvoiceRows(row, null, productService, null));
	};

	const selectEconomicProduct = (row, product: EconomicProduct) => {
		setInvoiceRows(getMappedInvoiceRows(row, null, null, product));
	};

	useEffect(() => {
		setIsReadyForExport(
			invoiceRows.every(row => {
				if (isXeroInvoice) {
					return !!row.xeroAccount;
				}

				if (isQuickbooksInvoice) {
					return !!row.quickbooksItem;
				}

				if (isEconomicInvoice) {
					return !!row.economicProduct;
				}

				return false;
			})
		);
	}, [invoiceRows]);

	const onSave = () => {
		if (invoice && billTo) {
			setLoading(true);

			const onSuccess = () => {
				setLoading(false);
				closeModal();
			};

			const onError: OnErrorType = (error, onFailure) => {
				// Workaround. Old error modal is shown behind new design-system modals - so we have to close it first.
				onSuccess();
				onFailure(error);
			};
			if (isXeroInvoice) {
				onExportXeroInvoice({
					intl,
					onSuccess,
					billTo,
					projectInDescription,
					invoiceRows,
					invoice,
					onError,
				});
			} else if (isQuickbooksInvoice) {
				exportInvoiceToQuickbooksOnline({
					intl,
					onSuccess,
					billTo,
					invoice,
					invoiceRows,
					quickbookSettings,
					taxCode: selectedQuickbooksCode,
					onError,
				});
			} else if (isEconomicInvoice) {
				exportInvoiceToEconomics({
					intl,
					onSuccess,
					billTo,
					invoice,
					invoiceRows,
					onError,
				});
			}
		}
	};

	const getProjectIdRenderProps = row => {
		return {
			color: row.projectColor,
			text: row.customProjectId,
		};
	};

	const getExportToName = () => {
		if (isXeroInvoice) {
			return 'Xero';
		}

		if (isQuickbooksInvoice) {
			return 'QuickBooks Online';
		}

		if (isEconomicInvoice) {
			return 'E-conomic';
		}

		return '';
	};

	const onQuickbookSettingChange = (updateIndex: number) => {
		setQuickbookSettings(
			quickbookSettings.map((item, index) => {
				if (index === updateIndex) {
					item.checked = !item.checked;
				}

				return item;
			})
		);
	};

	const getDropdownColumnItems = () => {
		if (isXeroInvoice) {
			return xeroAccounts;
		}

		if (isQuickbooksInvoice) {
			return quickbooksProductsServices;
		}

		if (isEconomicInvoice) {
			return economicProducts;
		}

		return [];
	};

	const disabledTooltipContent = !isReadyForExport ? (
		<FlexColumn>
			<SubHeading>
				<FormattedMessage
					id={
						isXeroInvoice
							? 'invoicing.xero.export_disabled_tooltip.title'
							: isEconomicInvoice
							? 'invoicing.economic.export_disabled_tooltip.title'
							: 'invoicing.quickbooks.export_disabled_tooltip.title'
					}
				/>
			</SubHeading>
			<Text>
				<FormattedMessage
					id={
						isXeroInvoice
							? 'invoicing.xero.export_disabled_tooltip.description'
							: isEconomicInvoice
							? 'invoicing.economic.export_disabled_tooltip.description'
							: 'invoicing.quickbooks.export_disabled_tooltip.description'
					}
				/>
			</Text>
		</FlexColumn>
	) : undefined;
	return (
		<Modal size={Modal.SIZE.EXTRA_EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>
				<Heading size={4}>
					<FormattedMessage id={'invoicing.export_to'} values={{systemName: getExportToName()}} />
				</Heading>
			</Modal.Title>

			<Modal.Content>
				<FlexColumn gap={'xxl'}>
					<FlexRow gap={'l'} alignItems={'start'}>
						<FlexColumn>
							<SubHeading>
								<FormattedMessage id={'common.client'} />
							</SubHeading>

							<Text ellipsis>{client?.name}</Text>
						</FlexColumn>

						<FlexColumn>
							<FlexRow alignItems={'center'}>
								<SubHeading>
									<FormattedMessage id={'settings_finance.bill_from_entity'} />
								</SubHeading>

								<IconTooltip hidden>
									<IconTooltip.Item
										title={formatMessage({id: 'settings_finance.bill_from_entity'})}
										details={formatMessage({id: 'invoicing.export.bill_from_entity_tooltip_details'})}
									/>
								</IconTooltip>
							</FlexRow>

							<Text ellipsis>{billTo?.billFrom?.name}</Text>
						</FlexColumn>

						<FlexColumn>
							<FlexRow alignItems={'center'}>
								<SubHeading>
									<FormattedMessage id={'settings_finance.bill_to_entity'} />
								</SubHeading>

								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({id: 'settings_finance.bill_to_entity'})}
										details={formatMessage({id: 'invoicing.export.bill_to_entity_tooltip_details'})}
									/>
								</IconTooltip>
							</FlexRow>

							<Text ellipsis>{billTo?.name}</Text>
						</FlexColumn>

						{isXeroInvoice && (
							<FlexColumn>
								<SubHeading>
									<FormattedMessage id={'integrations.xero.xero_account'} />

									<DropdownList>
										<DropdownList.Trigger>
											<ButtonDropdownField disabled={loading} isDefaultLabel={!selectedXeroAccount}>
												{selectedXeroAccount
													? selectedXeroAccount.label
													: formatMessage({id: 'common.select'}) +
													  ' ' +
													  formatMessage({id: 'integrations.xero.xero_account'})}
											</ButtonDropdownField>
										</DropdownList.Trigger>
										{xeroAccounts?.length > 0 && (
											<DropdownList.Content offset={10}>
												{xeroAccounts.map((xeroAccount, index) => (
													<DropdownList.Item
														key={`xero-${index}`}
														onClick={() => setSelectedXeroAccount(xeroAccount)}
													>
														{xeroAccount.label}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										)}
									</DropdownList>
								</SubHeading>
							</FlexColumn>
						)}

						{isQuickbooksInvoice && (
							<>
								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'integrations.quickbooks.tax_code'} />

										<DropdownList>
											<DropdownList.Trigger>
												<ButtonDropdownField
													disabled={loading}
													isDefaultLabel={!selectedQuickbooksCode}
												>
													{selectedQuickbooksCode
														? selectedQuickbooksCode.label
														: formatMessage({id: 'invoicing.no_tax'})}
												</ButtonDropdownField>
											</DropdownList.Trigger>
											{quickbooksTaxCodes?.length > 0 && (
												<DropdownList.Content offset={10}>
													{quickbooksTaxCodes.map((taxCode, index) => (
														<DropdownList.Item
															key={`tax-code-${index}`}
															onClick={() => setSelectedQuickbooksCode(taxCode)}
														>
															{taxCode.label}
														</DropdownList.Item>
													))}
												</DropdownList.Content>
											)}
										</DropdownList>
									</SubHeading>
								</FlexColumn>

								<FlexColumn>
									<SubHeading>
										<FormattedMessage id={'integrations.quickbooks.items_and_services'} />

										<DropdownList>
											<DropdownList.Trigger>
												<ButtonDropdownField
													disabled={loading}
													width={'320px'}
													isDefaultLabel={!selectedQuickbooksProductService}
												>
													{selectedQuickbooksProductService
														? selectedQuickbooksProductService.label
														: formatMessage({id: 'common.select'}) +
														  ' ' +
														  formatMessage({id: 'integrations.quickbooks.items_and_services'})}
												</ButtonDropdownField>
											</DropdownList.Trigger>
											{quickbooksProductsServices?.length > 0 && (
												<DropdownList.Content offset={10}>
													{quickbooksProductsServices.map((productService, index) => (
														<DropdownList.Item
															key={`product-service-${index}`}
															onClick={() => setSelectedQuickbooksProductService(productService)}
														>
															{productService.label}
														</DropdownList.Item>
													))}
												</DropdownList.Content>
											)}
										</DropdownList>
									</SubHeading>
								</FlexColumn>
							</>
						)}

						{isEconomicInvoice && (
							<FlexColumn>
								<SubHeading>
									<FormattedMessage id={'integrations.economic.product'} />

									<DropdownList>
										<DropdownList.Trigger>
											<ButtonDropdownField disabled={loading} isDefaultLabel={!selectedEconomicProduct}>
												{selectedEconomicProduct
													? selectedEconomicProduct.label
													: formatMessage({id: 'common.select'}) +
													  ' ' +
													  formatMessage({id: 'integrations.economic.product'})}
											</ButtonDropdownField>
										</DropdownList.Trigger>
										{economicProducts?.length > 0 && (
											<DropdownList.Content offset={10}>
												{economicProducts.map((product, index) => (
													<DropdownList.Item
														key={`economic-product-${index}`}
														onClick={() => setSelectedEconomicProduct(product)}
													>
														{product.label}
													</DropdownList.Item>
												))}
											</DropdownList.Content>
										)}
									</DropdownList>
								</SubHeading>
							</FlexColumn>
						)}
					</FlexRow>

					{isQuickbooksInvoice && (
						<FlexRow>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown emphasis={'medium'} style={{width: 'fit-content'}}>
										<FormattedMessage
											id={'invoicing.quickbooks.quickbook_settings'}
											values={{count: quickbookSettingsCount}}
										/>
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={10} align={'start'}>
									{quickbookSettings.map((setting, index) => (
										<DropdownList.CheckboxItem
											key={`quickbook-setting-${index}`}
											onChange={() => onQuickbookSettingChange(index)}
											checked={setting.checked}
											disabled={setting.disabled}
										>
											{setting.name}
										</DropdownList.CheckboxItem>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexRow>
					)}

					{isXeroInvoice && (
						<Checkbox
							checked={projectInDescription}
							onChange={() => setProjectInDescription(!projectInDescription)}
							labelText={formatMessage({id: 'invoicing.xero.project_in_description'})}
							disabled={loading}
						/>
					)}

					<Table data={invoiceRows} width={'100%'}>
						<Table.ColoredIdIconColumn
							accessorKey={'project.id'}
							header={formatMessage({id: 'common.id'})}
							renderProps={getProjectIdRenderProps}
						/>
						<Table.TextColumn accessorKey={'project.name'} header={formatMessage({id: 'common.project'})} />
						<Table.TextColumn
							accessorKey={'entryDescription'}
							width={'232px'}
							header={formatMessage({id: 'common.description'})}
						/>

						<Table.DropdownColumn
							header={dropdownColumnLabel}
							label={row => row.label || ''}
							defaultLabel={formatMessage({id: 'common.select'}) + ' ' + dropdownColumnLabel}
							width={'180px'}
						>
							{getDropdownColumnItems().map((item, index) => (
								<Table.DropdownColumn.ListItem
									key={`row-dropdown-${index}`}
									onClick={row => {
										if (isXeroInvoice) {
											selectExpenseXeroAccount(row, item);
										} else if (isQuickbooksInvoice) {
											selectQuickBooksProductService(row, item);
										} else if (isEconomicInvoice) {
											selectEconomicProduct(row, item);
										}
									}}
									disabled={loading}
								>
									{item.label}
								</Table.DropdownColumn.ListItem>
							))}
						</Table.DropdownColumn>

						<Table.NumberColumn accessorKey={'quantity'} header={formatMessage({id: 'common.quantity'})} />
						<Table.CurrencyColumn
							currency={invoice?.currency || ''}
							accessorKey={'unitPrice'}
							header={formatMessage({id: 'common.unit_price'})}
							maximumFractionDigits={4}
						/>
						<Table.PercentageColumn
							accessorKey={'discountPercentage'}
							header={formatMessage({id: 'project_section.xero_modal_table_discount'})}
						/>
						<Table.PercentageColumn accessorKey={'taxPercentage'} header={formatMessage({id: 'common.tax'})} />
						<Table.CurrencyColumn
							currency={invoice?.currency || ''}
							accessorKey={'totalPrice'}
							header={formatMessage({id: 'expense_item_modal.total_price'})}
						/>
					</Table>

					<FlexRow alignItems={'end'} alignSelf={'end'}>
						<FlexColumn gap={'s'}>
							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'common.subtotal'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.subtotal, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'project_section.xero_modal_table_discount'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.discount, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<FormattedMessage id={'common.tax'} />:
								<Text>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.tax, intl, noSpace)}
								</Text>
							</FlexRow>

							<FlexRow gap={'xxl'} justifyContent={'space-between'}>
								<SubHeading>
									<FormattedMessage id={'common.total_amount'} />
								</SubHeading>
								<SubHeading>
									{Util.getFormattedNumberWithCurrency(currencySymbol, invoiceTotals.total, intl, noSpace)}
								</SubHeading>
							</FlexRow>
						</FlexColumn>
					</FlexRow>
				</FlexColumn>
			</Modal.Content>

			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={onSave}
					tooltipContent={disabledTooltipContent}
					disabled={!isReadyForExport || loading}
				>
					<FormattedMessage id={'common.export'} />
				</Modal.PrimaryFooterButton>

				<Modal.SecondaryFooterButton onPress={closeModal} disabled={loading}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default createFragmentContainer(ExportInvoiceModal, exportInvoiceModalFragment);
